import { Box, useMediaQuery, useTheme, Dialog, IconButton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper } from "@mui/material";
import React, { useState, useMemo, useEffect } from "react";
import { COLORS } from "../../assets/theme";
import CustomLabel from "../../components/common/CustomLabel";
import CustomTable from "../../components/common/table/CustomTable";
import SearchHeaderContainer from "../../components/common/table/SearchHeaderContainer";
import { useWhiteListedIpsQuery } from "../../services/WhiteListedIpsService";
import { fetchAccountsAll } from "../../services/AccountService";
import { ReactComponent as IpIcon } from "../../assets/icons/ip.svg";
import { IField, Params, IPageable } from "../../types/table/interfaces.type";
import CloseIcon from "@mui/icons-material/Close";
interface IpAddressRow {
  ip: string;
  accountName?: string;
  accountIds?: string[];
}

const WhiteListedIps: React.FC = () => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const [appliedFilters, setAppliedFilters] = useState<Params<IpAddressRow>>(
    {}
  );
  const [refreshKey, setRefreshKey] = useState(0);
  const [nameDialogOpen, setNameDialogOpen] = useState(false);
  const [selectedAccounts, setSelectedAccounts] = useState<any[]>([]);
  const [selectedIp, setSelectedIp] = useState<string>("");
  const { data: apiIpData } = useWhiteListedIpsQuery();
  const [accountsData, setAccountsData] = useState<IPageable<any> | any[] | null>(null);

  useEffect(() => {
    fetchAccountsAll({}).then((data) => setAccountsData(data));
  }, []);

  const allIps = useMemo(() => {
    if (!apiIpData || !Array.isArray(apiIpData)) return [];
    const ipToAccountsMap = new Map<string, { names: string[]; ids: string[] }>();
    
    const accounts = Array.isArray(accountsData) 
      ? accountsData
      : [];
    if (accounts.length > 0) {
      accounts.forEach((account: any) => {
        if (account.acceptedIps) {
          const ips = Array.isArray(account.acceptedIps)
            ? account.acceptedIps
            : account.acceptedIps.split(",").map((ip: string) => ip.trim());
          
          ips.forEach((ip: string) => {
            if (ip) {
              if (!ipToAccountsMap.has(ip)) {
                ipToAccountsMap.set(ip, { names: [], ids: [] });
              }
              ipToAccountsMap.get(ip)!.names.push(account.name);
              ipToAccountsMap.get(ip)!.ids.push(account.id);
            }
          });
        }
      });
    }
    
    return apiIpData.map((ip) => {
      const accountData = ipToAccountsMap.get(ip);
      return {
        ip,
        accountName: accountData && accountData.names.length > 0 
          ? accountData.names.join(", ") 
          : "UNKNOWN",
        accountIds: accountData && accountData.ids.length > 0 
          ? accountData.ids 
          : undefined,
      };
    });
  }, [apiIpData, accountsData]);

  useEffect(() => {
    if (apiIpData && Array.isArray(apiIpData) && apiIpData.length > 0) {
      setRefreshKey((prev) => prev + 1);
    }
  }, [apiIpData, accountsData]);

  const getPaginatedIps = async (
    params: Params<IpAddressRow>
  ): Promise<IPageable<IpAddressRow>> => {
    const filteredIps = params.search
      ? allIps.filter((row) =>
          row.ip.toLowerCase().includes(params.search!.toLowerCase()) ||
          row.accountName?.toLowerCase().includes(params.search!.toLowerCase())
        )
      : allIps;

    const startIndex = (params.page || 0) * (params.size || 20);
    const endIndex = startIndex + (params.size || 20);
    const paginatedIps = filteredIps
      .slice(startIndex, endIndex);

    return {
      content: paginatedIps,
      totalElements: filteredIps.length,
      totalPages: Math.ceil(filteredIps.length / (params.size || 20)),
      number: params.page || 0,
      size: params.size || 20,
      numberOfElements: paginatedIps.length,
      first: (params.page || 0) === 0,
      last:
        (params.page || 0) >=
        Math.ceil(filteredIps.length / (params.size || 20)) - 1,
      empty: paginatedIps.length === 0,
    } as IPageable<IpAddressRow>;
  };

  const fields: IField<IpAddressRow>[] = [
    {
      key: "ip",
      header: "IP Address",
      transform: (row) => (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            width: "100%",
            height: "100%",
          }}
        >
          <CustomLabel
            text={row.ip}
            fontSize="14px"
            fontWeight={500}
            color={COLORS.typographyColor}
          />
        </Box>
      ),
    },
    {
      key: "accountName",
      header: "Account Name",
      transform: (row) => (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            width: "100%",
            height: "100%",
          }}
        >
          {row.accountIds && row.accountIds.length > 0 ? (
            <span
              style={{
                color: COLORS.GOLD,
                cursor: "pointer",
                textDecoration: "underline",
              }}
              onClick={() => handleNameClick(row)}
            >
              {row.accountName || "Unknown"}
            </span>
          ) : (
            <CustomLabel
              text={row.accountName || "Unknown"}
              fontSize="14px"
              fontWeight={500}
              color={COLORS.typographyColor}
            />
          )}
        </Box>
      ),
    },
  ];

  const handleNameClick = (row: IpAddressRow) => {
    if (row.accountIds && row.accountIds.length > 0 && accountsData) {
      const allAccounts = Array.isArray(accountsData) 
        ? accountsData 
        : (accountsData?.content && Array.isArray(accountsData.content) ? accountsData.content : []);
      const matchedAccounts = allAccounts.filter(
        (acc: any) => row.accountIds!.includes(acc.id)
      );
      if (matchedAccounts.length > 0) {
        setSelectedAccounts(matchedAccounts);
        setSelectedIp(row.ip);
        setNameDialogOpen(true);
      }
    }
  };

  return (
    <Box py={{ xs: 2, sm: 3, md: 4 }} px={{ xs: 2, sm: 3, md: 4 }} width="100%">
      <Box mb={3}>
        <Box display="flex" alignItems="center" gap={2} mb={1}>
          <Box
            sx={{
              p: 1,
              borderRadius: 2,
              backgroundColor: COLORS.activeBackground,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <IpIcon width={24} height={24} fill={COLORS.GOLD} />
          </Box>
          <Box sx={{ textAlign: "center" }}>
            <CustomLabel
              text="Whitelisted IPs"
              variant={isSmallScreen ? "h6" : "h4"}
              fontWeight={600}
              color={COLORS.typographyColor}
              sx={{ marginBottom: 1 }}
            />
            <CustomLabel
              text="IP addresses allowed into our server, pulled from server's IP Table directly."
              variant="body2"
              color="text.secondary"
            />
          </Box>
        </Box>
      </Box>

      <Box mb={2}>
        <SearchHeaderContainer
          search={appliedFilters.search ?? ""}
          setSearch={(value) =>
            setAppliedFilters((prev) => ({ ...prev, search: value }))
          }
        />
        <CustomTable<IpAddressRow>
          key={refreshKey}
          filters={appliedFilters}
          fields={fields}
          fetchFunc={getPaginatedIps}
        />
      </Box>

      <Dialog
        open={nameDialogOpen}
        onClose={() => setNameDialogOpen(false)}
        maxWidth="md"
        fullWidth
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          "& .MuiDialog-container": {
            alignItems: "center",
          },
        }}
      >
        <Box p={3} position="relative">
          <IconButton
            aria-label="close"
            onClick={() => setNameDialogOpen(false)}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
            size="medium"
          >
            <CloseIcon />
          </IconButton>
          <CustomLabel
            text={`Accounts for IP: ${selectedIp}`}
            fontWeight={600}
            fontSize="1.2rem"
            sx={{ mb: 2 }}
          />
          {selectedAccounts.length > 0 && (
            <TableContainer component={Paper} sx={{ backgroundColor: COLORS.activeBackground }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, color: COLORS.typographyColor }}>Name</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: COLORS.typographyColor }}>Username</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: COLORS.typographyColor }}>Type</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: COLORS.typographyColor }}>TPS</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: COLORS.typographyColor }}>Hard TPS</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: COLORS.typographyColor }}>Active</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {selectedAccounts.map((account, index) => (
                    <TableRow key={account.id || index}>
                      <TableCell sx={{ color: COLORS.typographyColor }}>{account.name || "-"}</TableCell>
                      <TableCell sx={{ color: COLORS.typographyColor }}>{account.systemId || "-"}</TableCell>
                      <TableCell sx={{ color: COLORS.typographyColor }}>{account.type || "-"}</TableCell>
                      <TableCell sx={{ color: COLORS.typographyColor }}>{account.tps ?? "-"}</TableCell>
                      <TableCell sx={{ color: COLORS.typographyColor }}>{account.tpsHardLimit ?? "-"}</TableCell>
                      <TableCell sx={{ color: COLORS.typographyColor }}>{account.active ? "Yes" : "No"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      </Dialog>
    </Box>
  );
};

export default WhiteListedIps;
